/**
 * Created by Jerker Nord on 2021-01-27.
 */
import {NolimitAudioPlayer} from "@nolimitcity/slot-audio/bin/NolimitAudioPlayer";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {NolimitHowl} from "@nolimitcity/slot-audio/bin/NolimitHowl";
import {NamedLogger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {gsap} from "gsap";
import {Outsourced} from "../Outsourced";

// if no volume is specified it defaults to 1.0
const OutsourcedSoundConfig:{[sound:string]:number} = {
    "gameStartOneShot": 1.0,
    "xWaysRevealDead": 1.0
}

export class OutsourcedSound {

    public static INTRO_MUSIC = "loaderLoop";
    public static REEL_SPINNING:string = "reelsSpinning"
    public static REEL_LANDING1 = "reelLanding1";
    public static REEL_LANDING2 = "reelLanding2";
    public static REEL_SPINNING_FS:string = "reelsSpinningBonus";

    public static BASE_GAME_MUSIC:string = "background_music_main";
    public static FREESPINS_MUSIC:string = "background_music_fs";
    public static SUPER_FREESPINS_MUSIC:string = "background_music_sfs";

    public static X_BET_TOGGLE:string = "xBetOnOff";

    private _currentMusic:string;
    private player:NolimitAudioPlayer;
    private logger:NamedLogger;

    static WIN_TINY_START:string = "winStart";
    static WIN_SMALL_START:string = "winStart";
    static WIN_MEDIUM_START:string = "winStart";
    static WIN_LARGE_START:string = "winStart";
    static WIN_TINY:string = "winTiny";
    static WIN_SMALL:string = "winSmall";
    static WIN_MEDIUM:string = "winMediumCountUp";
    static WIN_LARGE:string = "winLargeCountUp";
    static WIN_TINY_END:string = "";
    static WIN_SMALL_END:string = "";
    static WIN_MEDIUM_END:string = "winMediumEnd";
    static WIN_LARGE_END:string = "winLargeEnd";

    static BIG_WIN_END:string = "bigWinEnd";
    static BIG_WIN_CHANGE:string = "bigWinLevelChange";
    static CONTINUE_BUTTON:string = "continueButton";
    static DOUBLE_OR_NOTHING_YES:string = "maxWinDoubleYes";

    static FS_INTRO:string = "fsIntro";


    private _previousSound: string;
    private _stopRepeatSoundTL: gsap.core.Timeline;
    private _stopRepeatSound: boolean;
    private alreadyPlayingSound:Set<string>;


    constructor() {
        this.player = <NolimitAudioPlayer>SlotGame.sound;
        this.logger = new NamedLogger("DCSound");
        this.alreadyPlayingSound = new Set<string>();

        this._stopRepeatSoundTL = gsap.timeline();
        this._stopRepeatSoundTL.add((): void => {
            this._previousSound = "";
            this._stopRepeatSound = false;
        }, "+=0.1");
    }


    private static createConfig(name:string, volume?:number, fVolume?:number, repeat?:number, fadeDur?:number) {
        return {
            name:name,
            volume:volume,
            fVolume:fVolume,
            repeat:repeat,
            fadeDurr:fadeDur
        }
    }

    public playLoopingSound(name:string, volume?:number, fadeVolume?:number, repeat?:number, fadeDuration?:number):void {
        this.playAndGetLoopingSound(name, volume, fadeVolume, repeat, fadeDuration);
    }

    public playAndGetLoopingSound(name:string, volume?:number, fadeVolume?:number, repeat?:number, fadeDuration?:number):NolimitHowl | undefined {
        const sound = this.playAndGetSound(name, volume, fadeVolume, repeat, fadeDuration);
        if (sound) {
            sound.setLoop(true);
        }
        return sound;
    }

    public playSound(name:string, volume?:number, fadeVolume?:number, repeat?:number, fadeDuration?:number):void {
        if (!this.alreadyPlayingSound.has(name)) {
            this.playAndGetSound(name, volume, fadeVolume, repeat, fadeDuration);
            this.alreadyPlayingSound.add(name);
            gsap.timeline().add(() => {
                this.alreadyPlayingSound.delete(name)
            }, '>0.01');
        }
    }

    public playSoundBased(name:string):gsap.core.Timeline {
        return gsap.timeline().add(() => {
            const volume = OutsourcedSoundConfig[name] != undefined ? OutsourcedSoundConfig[name] : 1.0;
            this.playSound(name, volume);
        })
    }

    public playLoopBased(name:string):gsap.core.Timeline {
        return gsap.timeline().add(() => {
            const volume = OutsourcedSoundConfig[name] != undefined ? OutsourcedSoundConfig[name] : 1.0;
            this.playLoopingSound(name, volume);
        })
    }
    public stopLoopBased(name:string):gsap.core.Timeline {
        return gsap.timeline().add(() => {
            this.stopSound(name);
        })
    }

    public playAndGetSound(name:string, volume?:number, fadeVolume?:number, repeat?:number, fadeDuration?:number):NolimitHowl | undefined {
        this.logger.log("playSound: " + name);
        return this.player.playTrack(OutsourcedSound.createConfig(name, volume, fadeVolume, repeat, fadeDuration));
    }

    public stopSound(name:string, fadeOutMS:number = 0) {
        if (fadeOutMS > 0) {
            const tl = gsap.timeline();
            tl.add(() => {
                this.player.fadeTrack({name:name, to:0, duration:fadeOutMS})
            });
            tl.add(() => {
                this.player.stopTrack(name)
            }, fadeOutMS * 0.001);

        } else {
            this.player.stopTrack(name);
        }
    }

    public switchMusic(mode:GameMode | string) {
        let nextMusic = "";

        switch (mode) {
            case "INTRO":
                nextMusic = OutsourcedSound.INTRO_MUSIC;
                break;
            case "FS_INTRO":
                nextMusic = OutsourcedSound.FS_INTRO;
                break;
            case "FS_OUTRO":
                nextMusic = OutsourcedSound.FS_INTRO;
                break;
            case GameMode.FREESPIN:
            case "FREESPIN_RESPIN":
                nextMusic = Outsourced.model.data.scatterCount == 4
                            ? OutsourcedSound.SUPER_FREESPINS_MUSIC
                            : OutsourcedSound.FREESPINS_MUSIC;
                break;
            case GameMode.FREESPIN_SUPER:
                nextMusic = OutsourcedSound.SUPER_FREESPINS_MUSIC;
                break;
            default:
                nextMusic = OutsourcedSound.BASE_GAME_MUSIC;
                break;
        }

        if (nextMusic == this._currentMusic) {
            return;
        }

        if (this._currentMusic) {
            this.player.stopTrack(this._currentMusic);
        }

        this.player.playTrack(OutsourcedSound.createConfig(nextMusic, 1, undefined));
        this._currentMusic = nextMusic;
    }

    public fadeOutMusic(duration:number = 300, targetVolume:number = 0) {
        if (this._currentMusic) {
            this.player.fadeTrack({
                name:this._currentMusic,
                to:targetVolume,
                duration:duration
            });
        }
    }

    public fadeInMusic(duration:number = 300) {
        if (this._currentMusic) {
            this.player.fadeTrack({
                name:this._currentMusic,
                to:1,
                duration:duration
            });
        }
    }

}